import React, {useEffect, useState} from 'react';
import styles from "./positionsWrap.module.scss";
import {usePositions} from "../../../hooks/positions.hook";
import {IStrategiesInfo, OpenedPositions, TimeFrames} from "../../../shared/types";
import {message as messageAnt} from "antd";
import {transformAllPositions} from "../../../shared/lib/lib";
import {v4 as uuidv4} from 'uuid';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faXmark} from '@fortawesome/free-solid-svg-icons'
import {CountryExchange} from "../../../shared/countryExchange";
import {useSettings} from "../../../hooks/settings.hook";

interface PositionsForRender {
    strategy: string
    ticker: string
    timeFrame: string
    value: string
    price: string
}

interface Props {
    country: CountryExchange
    tickerList: string[]
}

const PositionsWrap = ({country, tickerList}: Props) => {

    const {loading: loadingPositions, saveOpenedPositions, removeOpenedPositions, getAllPositions, error: errorPositions, clearError: clearErrorPositions} = usePositions()
    const {loading: loadingSettings, getSettingsByStrategyList, saveSettingsForStrategy, getAllStrategiesInfo, error: errorSettings, clearError: clearErrorSettings} = useSettings()

    const [strategiesInfo, setStrategiesInfo] = useState<IStrategiesInfo | undefined>()
    const [openedPositions, setOpenedPositions] = useState<typeof OpenedPositions>()
    //список открытых позиций, подготовленный для рендера
    const [openedPositionsForRender, setOpenedPositionsForRender] = useState<Array<PositionsForRender>>()

    //функция получение информации по существующим стратегиям
    const getStrategiesInfo = async () => {
        try {
            const data = await getAllStrategiesInfo()
            messageAnt.info(data.message)
            return data.info
        } catch (e) {
            messageAnt.info(errorSettings);
            clearErrorSettings()
            return undefined
        }
    }

    //получение информации по существующим стратегиям
    useEffect(() => {
        const get = async () => {
            const info = await getStrategiesInfo()
            setStrategiesInfo(info)
        }
        get()
    }, [])

    //функция преобразование Record с открытыми позициями в обычный массив строк
    const getOpenedPositionsList = (openedPositions: typeof OpenedPositions): Array<PositionsForRender> => {
        const arr: Array<PositionsForRender> = []
        if (openedPositions && strategiesInfo) {
            Object.keys(strategiesInfo).forEach(strategy => {
                tickerList.forEach(ticker => {
                    Object.keys(TimeFrames).forEach(timeFrame => {
                        if (
                            openedPositions &&
                            openedPositions[strategy] &&
                            openedPositions[strategy][ticker] &&
                            openedPositions[strategy][ticker][timeFrame as keyof typeof TimeFrames]
                        ) {
                            const enters = openedPositions[strategy][ticker][timeFrame as keyof typeof TimeFrames].enters
                            const exits = openedPositions[strategy][ticker][timeFrame as keyof typeof TimeFrames].exits

                            enters.forEach((enter, index) => {
                                if (index === 0) {
                                    arr.push({
                                        strategy: strategy,
                                        ticker: ticker,
                                        timeFrame: timeFrame,
                                        value: '+' + enter.value.toString(),
                                        price: enter.price ? enter.price.toString() : ' '
                                    })
                                } else {
                                    arr.push({
                                        strategy: '',
                                        ticker: '',
                                        timeFrame: '',
                                        value: '+' + enter.value.toString(),
                                        price: enter.price ? enter.price.toString() : ' '
                                    })
                                }
                            })
                            exits.forEach((exit, index) => {
                                arr.push({
                                    strategy: '',
                                    ticker: '',
                                    timeFrame: '',
                                    value: '-' + exit.value.toString(),
                                    price: exit.price ? exit.price.toString() : ' '
                                })
                            })
                        }
                    })
                })
            })
        }
        return arr
    }

    //преобразование Record с открытыми позициями в обычный массив строк, для рендера
    useEffect(() => {
        if (openedPositions && strategiesInfo) {
            const openedPositionsForRender = getOpenedPositionsList(openedPositions)
            setOpenedPositionsForRender(openedPositionsForRender)
        }
    }, [openedPositions, strategiesInfo])

    //получение данных по открытым позициям для всех стратегий, таймфреймов и инструментов из БД для пользователя
    const getOpenedPositions = async (country: CountryExchange) => {
        try {
            const data = await getAllPositions(country)
            messageAnt.info(data.message)
            const positions = transformAllPositions(data.info)
            return positions
        } catch (e) {
            messageAnt.info(errorPositions);
            clearErrorPositions()
            return undefined
        }
    }

    //изначальное получение данных по открытым позициям из БД для пользователя
    useEffect(() => {
        if (country) {
            const get = async () => {
                const openedPositions = await getOpenedPositions(country)
                setOpenedPositions(openedPositions)
            }
            get()
        }
    }, [country])

    //функция для сохранение данных по открытым позициям в БД для одной стратегии в которой были изменения по позициям по любому тикеру и тф
    const savePositions = async (country: CountryExchange, strategy: string, positions: string) => {
        if (!positions) {
            //если последняя позиция по стратегии закрыта, то очищаем документ по этой стратегии в БД
            try {
                const data = await removeOpenedPositions(country, strategy)
                messageAnt.info(data.message)
            } catch (e) {
                messageAnt.info(errorPositions);
                clearErrorPositions()
            }
        } else {
            try {
                const data = await saveOpenedPositions(country, strategy, positions)
                messageAnt.info(data.message)
            } catch (e) {
                messageAnt.info(errorPositions);
                clearErrorPositions()
            }
        }
    }

    const deletePositionHandler = async (index: number) => {
        if (openedPositions && openedPositionsForRender && country) {

            const areYouSure = window.confirm(`Вы уверены, что хотите удалить информацию об открытой позиции по ${openedPositionsForRender[index].ticker}?`)
            if (!areYouSure) {
                return
            }

            //что будем удалять
            const strategy = openedPositionsForRender[index].strategy
            const ticker = openedPositionsForRender[index].ticker
            const timeframe = openedPositionsForRender[index].timeFrame

            let newOpenedPositions
            newOpenedPositions = JSON.parse(JSON.stringify(openedPositions))
            delete newOpenedPositions[strategy][ticker][timeframe]
            if (Object.keys(newOpenedPositions[strategy][ticker]).length === 0) {
                delete newOpenedPositions[strategy][ticker]
                if (Object.keys(newOpenedPositions[strategy]).length === 0) {
                    delete newOpenedPositions[strategy]
                }
            }
            try {
                //сохранение обновленной информации по открытым позициям в БД
                await savePositions(country, strategy, JSON.stringify(newOpenedPositions[strategy]))
                setOpenedPositions(newOpenedPositions)
            } catch (e) {

            }
        }
    }

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <div className={styles.wrap}>
                    <h5>Открытые позиции</h5>
                    {
                        country && openedPositionsForRender ?
                            <div className={[styles.received, styles.blockWrap].join(' ')}>
                                <p className={styles.title}>Открытые позиции по всем инструментам и стратегиям</p>
                                <div className={styles.headTable}>
                                    <p className={styles.ticker}>Инструмент</p>
                                    <p className={styles.strategy}>Стратегия</p>
                                    <p className={styles.timeFrame}>Тайм фрейм</p>
                                    <p className={styles.value}>Объем</p>
                                    <p className={styles.price}>Цена</p>
                                    <p className={styles.delete}></p>
                                </div>
                                <div className={styles.bodyTable}>
                                    {
                                        strategiesInfo &&
                                        openedPositionsForRender.map((position, index) => (
                                            <div className={styles.lineTable} key={uuidv4()}>
                                                <p className={styles.ticker}>{position.ticker}</p>
                                                <p
                                                    className={styles.strategy}
                                                    title={position.strategy ? strategiesInfo[position.strategy].label : ''}
                                                >
                                                    {position.strategy ? strategiesInfo[position.strategy].label : ''}
                                                </p>
                                                <p className={styles.timeFrame}>{position.timeFrame}</p>
                                                <p className={styles.value}>{position.value}</p>
                                                <p className={styles.price}>{position.price}</p>
                                                <p className={styles.delete}>
                                                    {
                                                        position.strategy &&
                                                        <span onClick={() => deletePositionHandler(index)}>
                                                            <FontAwesomeIcon icon={faXmark}/>
                                                        </span>
                                                    }
                                                </p>
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                            :
                            null
                    }
                </div>
            </div>
        </div>
    );
};

export default PositionsWrap;